// ./src/components/features/EmailTemplates/EmailTemplatesTable/EmailTemplatesTable.tsx
import React, { useMemo } from "react";
import { Text, Badge, Group } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
  AppButton,
} from "@/components/generic";
import { EmailTemplate, isSystemTemplate } from "@/utils/api";
import { STATUSES } from "@sdflc/utils";

export interface EmailTemplatesTableProps {
  /** Array of email templates to display */
  emailTemplates: EmailTemplate[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditEmailTemplate?: (emailTemplate: EmailTemplate) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateEmailTemplate?: (emailTemplate: EmailTemplate) => void;

  /** Callback when preview action is clicked */
  onPreviewEmailTemplate?: (emailTemplate: EmailTemplate) => void;

  /** Callback when remove action is clicked */
  onRemoveEmailTemplate?: (emailTemplate: EmailTemplate) => void;

  /** Callback when publish action is clicked */
  onPublishEmailTemplate?: (emailTemplate: EmailTemplate) => void;

  /** ID of email template currently being removed */
  removingEmailTemplateId?: string | null;

  /** ID of email template currently being published */
  publishingEmailTemplateId?: string | null;

  /** ID of email template just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const EmailTemplatesTable: React.FC<EmailTemplatesTableProps> = ({
  emailTemplates,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditEmailTemplate,
  onDuplicateEmailTemplate,
  onPreviewEmailTemplate,
  onRemoveEmailTemplate,
  onPublishEmailTemplate,
  removingEmailTemplateId,
  publishingEmailTemplateId,
  editedItemId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<EmailTemplate>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Group gap="xs">
              <Text fw={500}>{value || "Untitled Template"}</Text>
              {isSystemTemplate(record) && (
                <Badge variant="light" color="blue" size="xs">
                  System
                </Badge>
              )}
              {record.isDefault && (
                <Badge variant="light" color="green" size="xs">
                  Default
                </Badge>
              )}
            </Group>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "templateType",
        label: "Type",
        sortable: true,
        width: 140,
        minWidth: 120,
        align: "left",
        render: (value) => {
          if (!value) return <Text c="dimmed">—</Text>;

          // Capitalize first letter
          const displayValue = value.charAt(0).toUpperCase() + value.slice(1);

          return (
            <Badge variant="outline" color="gray" size="sm">
              {displayValue}
            </Badge>
          );
        },
      },
      {
        key: "emailLayout",
        label: "Layout",
        sortable: false,
        width: 180,
        minWidth: 150,
        align: "left",
        render: (_, record) => {
          const layoutName = record.emailLayout?.name;
          if (!layoutName) return <Text c="dimmed">None</Text>;
          return (
            <Text size="sm" lineClamp={1}>
              {layoutName}
            </Text>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 280,
        minWidth: 280,
        align: "right",
        render: (_, record: EmailTemplate) => {
          const isSystem = isSystemTemplate(record);
          const isRemoving = removingEmailTemplateId === record.id;
          const isPublishing = publishingEmailTemplateId === record.id;
          const isDraft = record.status === STATUSES.DRAFT;
          const canPublish = isDraft && !isSystem && onPublishEmailTemplate;

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              {canPublish && (
                <AppButton
                  variant="secondary"
                  size="xs"
                  iconName="Send"
                  onClick={() => onPublishEmailTemplate(record)}
                  disabled={isRemoving || isPublishing}
                  loading={isPublishing}
                >
                  Publish
                </AppButton>
              )}
              <RecordActions
                context={record}
                onEditClick={isSystem ? undefined : onEditEmailTemplate}
                onRemoveClick={isSystem ? undefined : onRemoveEmailTemplate}
                onDuplicateClick={onDuplicateEmailTemplate}
                onViewClick={onPreviewEmailTemplate}
                removeInProgress={isRemoving}
                editTooltip={
                  isSystem ? "System templates cannot be edited" : undefined
                }
                removeTooltip={
                  isSystem ? "System templates cannot be removed" : undefined
                }
              />
            </Group>
          );
        },
      },
    ],
    [
      onEditEmailTemplate,
      onDuplicateEmailTemplate,
      onPreviewEmailTemplate,
      onRemoveEmailTemplate,
      onPublishEmailTemplate,
      removingEmailTemplateId,
      publishingEmailTemplateId,
    ]
  );

  return (
    <AppTable
      data={emailTemplates}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(emailTemplate) => emailTemplate?.id || ""}
      highlightRow={(emailTemplate) => emailTemplate?.id === editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
